import React, { useMemo, useState } from 'react';
import { Row } from 'react-bootstrap';
import {
  FormCol,
  FormFilterCard,
  FieldInput,
  FieldSelectIsActive,
  FieldDateRanger,
  buildWhereForDates,
} from 'motion-components';
import { useTranslation } from 'react-i18next';
import {
  FieldSelectGroup,
  FieldSelectNomos,
  FieldSelectRegion,
  FieldSelectSubregion,
} from 'shared-pages';
import {
  isAfisCyprus,
  isFotokiklosi,
  isMedicycle,
  isRebattery,
} from 'shared-pages/common/constants';
import { Company } from 'shared-pages/common/constants';
import FsCategoryGroup from 'shared-pages/components/field-select/fs-categorygroup';

/* ----------------------- Διαχείριση Φίλτρων ------------------------------ */
export function buildWhereFromFilterValues(values): string {
  // Φτιάξε το where για το graphql
  let where = {};
  if (values) {
    // comma separated values
    if (values.searchText) {
      const searchTerms = values.searchText
        .split(',')
        .map((term) => term.trim())
        .filter((term) => term !== '');

      if (searchTerms.length > 0) {
        where = {
          ...where,
          $and: searchTerms.map((term) => {
            const likeObj = { $like: `%${term}%` };
            return {
              $or: [
                { code: likeObj },
                { name: likeObj },
                { addressLine1: likeObj },
                { addressArea: likeObj },
                { addressCity: likeObj },
                { phone: likeObj },
                { companyVat: likeObj },
                { companyTaxOffice: likeObj },
              ],
            };
          }),
        };
      }
    }
    if (values.startDate || values.endDate) {
      const firstDate = buildWhereForDates(values.startDate, values.endDate, 'firstDate');
      const lastDate = buildWhereForDates(values.startDate, values.endDate, 'lastDate');
      where = {
        ...where,
        $and: {
          $or: [firstDate, lastDate],
        },
      };
    }

    if (values.isActive !== undefined && values.isActive !== null && values.isActive !== '') {
      where = { ...where, isActive: values.isActive };
    }
    if (values.groupId) {
      where = { ...where, groupId: values.groupId };
    }
    if (values.subgroupId) {
      where = { ...where, subgroupId: values.subgroupId };
    }
    if (values.subsubgroupId) {
      where = { ...where, subsubgroupId: values.subsubgroupId };
    }
    if (values.parentCpId) {
      where = { ...where, parentCpId: values.parentCpId };
    }
    if (values.regionId) {
      where = { ...where, regionId: values.regionId };
    }
    if (values.subregionId) {
      where = { ...where, subregionId: values.subregionId };
    }
    if (values.addressNomos) {
      where = { ...where, addressNomos: values.addressNomos };
    }
    if (values.hasGpsCoordinates !== undefined) {
      if (values.hasGpsCoordinates) {
        where = {
          ...where,
          $and: [{ latitude: { $gt: 0 } }, { longitude: { $gt: 0 } }],
        };
      } else {
        where = {
          ...where,
          $and: [
            {
              $or: [
                { latitude: { $eq: 0 } },
                { latitude: { $eq: null } },
                { longitude: { $eq: 0 } },
                { longitude: { $eq: null } },
              ],
            },
          ],
        };
      }
    }
    if (
      values.isPubliclyVisibleInMap !== undefined &&
      values.isPubliclyVisibleInMap !== null &&
      values.isPubliclyVisibleInMap !== ''
    ) {
      where = { ...where, isPubliclyVisibleInMap: values.isPubliclyVisibleInMap };
    }
  }
  return JSON.stringify(where);
}

/* ----------------------- Filter Form ------------------------------ */
export const Filters = ({ onChange, initialValues, disable = false, isGridInForm }) => {
  //

  const { t } = useTranslation();
  function handleFiltersSubmit(values, { setSubmitting }) {
    if (disable) return;
    setTimeout(() => {
      setSubmitting(false);
      onChange(values);
    }, 400);
  }

  const filters = {
    searchText: initialValues?.searchText ?? '',
    isActive: initialValues?.isActive ?? null,
    groupId: initialValues?.groupId ?? undefined,
    subgroupId: initialValues?.subgroupId,
    subsubgroupId: initialValues?.subsubgroupId,
    regionId: initialValues?.regionId ?? undefined,
    subregionId: initialValues?.subregionId ?? undefined,
    addressNomos: initialValues?.addressNomos ?? undefined,
    hasGpsCoordinates: initialValues?.hasGpsCoordinates ?? undefined,
    isPubliclyVisibleInMap: initialValues?.isPubliclyVisibleInMap ?? undefined,
    startDate: initialValues?.startDate ?? null,
    endDate: initialValues?.endDate ?? null,
  };

  // To avoid re-rendering
  const [initialText, setInitialText] = useState('');
  const GroupsSelect = useMemo(
    () => (
      <FsCategoryGroup
        initialText={initialText}
        setInitialText={setInitialText}
        search={true}
      ></FsCategoryGroup>
    ),
    [initialText],
  );

  return (
    <FormFilterCard
      onClear={() => setInitialText('')}
      title={t('Κριτήρια Αναζήτησης')}
      onSubmit={handleFiltersSubmit}
      initialValues={filters}
    >
      <Row>
        <FieldInput
          as={FormCol}
          name="searchText"
          label={t('Αναζήτηση ( Κωδ., Επων., Διεύθ., Περιοχή, Πόλη, Τηλ., ΑΦΜ, ΔΟΥ )')}
          type="search"
          disable={disable}
        />
        {isFotokiklosi && <FieldSelectNomos as={FormCol} />}
        {(isRebattery || isAfisCyprus || isMedicycle) && <FieldSelectRegion as={FormCol} />}
        {isRebattery && <FieldSelectSubregion as={FormCol} />}
        {(isRebattery || isFotokiklosi) && <FieldSelectGroup as={FormCol} label="Κατηγορία" name="groupId" />}
        {isAfisCyprus && GroupsSelect}
        <FieldSelectIsActive label={t('Ενεργές Εγγραφές')} disable={disable} />
        <FieldSelectIsActive
          label={t('Με Γεωγραφικές Συντεταγμένες')}
          name="hasGpsCoordinates"
          disable={disable}
        />
        <FieldSelectIsActive
          label={t('Ορατά στον Χάρτη')}
          name="isPubliclyVisibleInMap"
          disable={disable}
        />
        <FieldDateRanger
          as={FormCol}
          label="Δημιουργία ή Αλλαγή από"
          name="startDate"
          name2="endDate"
        />
      </Row>
    </FormFilterCard>
  );
};
